/**
 * The Document Form (obj_type `E`) designer.
 *
 * Three tabs, and the split between them is the load-bearing part of the
 * design rather than a layout choice:
 *
 *  * **Settings** — governance, the document's KEYS, the TRANSACTIONS it
 *    covers, and where typed values go. The keys come first because everything
 *    else is looked up by them (§4A);
 *  * **Fields** — the SCHEMA: type, mandatory, tolerance, where the field
 *    writes (`TXN.form.COLUMN`), its formula, and the binding that fills a
 *    merge field. This is where a tolerance lives, deliberately, so a QA author
 *    editing body text at run time cannot change one;
 *  * **Body** — the laid-out document, edited in the SAME TipTap schema the
 *    DMS authoring editor uses, with a toolbar that inserts the three atoms.
 *    A node carries only a key; everything else is on the Fields tab.
 *
 * The body editor here is the TEMPLATE editor. The operator's fill surface is
 * `@twasta/page-runtime/er`, mounted by the runtime, so a template author and
 * an operator look at the same document laid out by the same engine.
 */

import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type { EmbeddedDesignerProps } from '../embeddedDesigner'
import {
  documentFormProblems, loadDocumentForm, saveDocumentForm,
} from '@/api/er'
import { objectCatalog } from '@/api/designers'
import { ddlbSelectValue } from '@twasta/page-runtime/txn'
import type { FormModel, FormTemplate, KeyRow, TxnRow } from './types'
import { adoptModel } from './adopt'
import FieldsTab from './FieldsTab'
import SectionsTab from './SectionsTab'
import SignOffTab from './SignOffTab'
import { buttonStyle, inputStyle } from './styles'

/**
 * LAZY: the body editor carries the TipTap stack, and this designer is
 * statically imported by `App.tsx` and the workbench Prototype tab. A static
 * import would put that stack in the main graph for everybody, which is what
 * made `vite build` run out of heap while rendering chunks. The DMS keeps the
 * same editor behind the same kind of boundary (`DocumentRunPane`).
 */
const BodyTab = lazy(async () => ({
  default: (await import('./BodyTab')).default,
}))

type Tab = 'settings' | 'sections' | 'fields' | 'body' | 'sign-off'

export default function DocumentFormDesignerPage({
  project, objName, onDirtyChange, registerSave, onSaved, initialTab,
}: EmbeddedDesignerProps & {
  project: string; objName: string; initialTab?: Tab
}) {
  const [model, setModel] = useState<FormModel | null>(null)
  /*
   * Settings first — unless the host has already answered Settings.
   *
   * Opened from the Visual designer's "Design document", the source, the
   * identity and the keys were all just written out there; landing on the
   * Settings tab showed a form of read-only facts and left the author
   * wondering where the document was.
   */
  const [tab, setTab] = useState<Tab>(initialTab || 'settings')
  /**
   * WHICH template is on screen.
   *
   * A form used to have exactly one, and every tab wrote into `templates[0]`.
   * A composed document has several — one master per variant, plus a section
   * template per piece — and they are edited through the SAME Fields and Body
   * tabs rather than through a second editor, because a section is an ordinary
   * template and giving it its own surface would be two of everything to keep
   * in step.
   */
  const [templateKey, setTemplateKey] = useState('')
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState(false)
  const [objects, setObjects] = useState<{ obj_name: string }[]>([])
  const [visuals, setVisuals] = useState<{ obj_name: string }[]>([])
  const [problems, setProblems] = useState<
    { code: string; message: string; blocking?: boolean }[]>([])
  const modelRef = useRef<FormModel | null>(null)
  modelRef.current = model

  useEffect(() => {
    let alive = true
    setBusy(true)
    loadDocumentForm(project, objName)
      // Through `adoptModel`, never raw. The server normalises and always
      // will, but a designer that throws on an unexpected payload fails on the
      // one screen somebody would fix that payload from — and it did exactly
      // that against a backend still serving the older normaliser, which
      // answers with an `anchor` and no `open`.
      .then((got) => {
        if (!alive) return
        setModel(adoptModel(got, objName))
        setDirty(false)
      })
      .catch((exc) => toast.error(exc?.response?.data?.detail
        || 'This document form could not be loaded.'))
      .finally(() => { if (alive) setBusy(false) })
    return () => { alive = false }
  }, [project, objName])

  // The sources this form could be built on. A document form is an interface
  // onto an object, so the source is CHOSEN from what exists rather than typed
  // — a typo here is a form that renders and never resolves.
  //
  // Two catalogues, because a document may be a face on a TRANSACTION (one
  // record, filled in) or on a VISUAL (a result set, one document per
  // identity). They are fetched together and kept apart, so the picker can say
  // which is which rather than presenting one undifferentiated list.
  useEffect(() => {
    let alive = true
    Promise.allSettled([
      objectCatalog(project, 'T'),
      objectCatalog(project, 'V'),
    ]).then(([t, v]) => {
      if (!alive) return
      setObjects(t.status === 'fulfilled' ? (t.value.objects || []) : [])
      setVisuals(v.status === 'fulfilled' ? (v.value.objects || []) : [])
    })
    return () => { alive = false }
  }, [project])

  const mutate = useCallback((fn: (m: FormModel) => void) => {
    setModel((prev) => {
      if (!prev) return prev
      const next = JSON.parse(JSON.stringify(prev)) as FormModel
      fn(next)
      return next
    })
    setDirty(true)
  }, [])

  const save = useCallback(async (): Promise<boolean> => {
    const current = modelRef.current
    if (!current) return false
    setBusy(true)
    try {
      await saveDocumentForm(project, objName,
                             current as unknown as Record<string, unknown>)
      setDirty(false)
      toast.success('Document form saved')
      onSaved?.()
      return true
    } catch (exc) {
      const detail = (exc as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail
      // A governance claim that does not hold comes back as a 422 with a
      // message that names exactly what to change. Showing it verbatim is the
      // whole point of refusing rather than ignoring it.
      toast.error(detail || 'This document form could not be saved.')
      return false
    } finally {
      setBusy(false)
    }
  }, [project, objName, onSaved])

  useEffect(() => { onDirtyChange?.(dirty) }, [dirty, onDirtyChange])
  useEffect(() => { registerSave?.(save) }, [registerSave, save])

  /**
   * What the readiness rail reports, from the server's own rule set.
   *
   * Debounced because it runs on the working copy and the working copy changes
   * on every keystroke in the body. 400 ms is long enough that typing a
   * sentence is one round trip and short enough that fixing a problem clears
   * it while the author is still looking at it.
   */
  useEffect(() => {
    if (!model) return undefined
    let alive = true
    const timer = setTimeout(() => {
      documentFormProblems(project, objName,
        model.form as unknown as Record<string, unknown>)
        .then((got) => { if (alive) setProblems(got) })
        // A rail that cannot reach the server says nothing rather than
        // guessing — an empty rail is "unknown", and it must never read as
        // "ready" on the strength of a failed request.
        .catch(() => { if (alive) setProblems([]) })
    }, 400)
    return () => { alive = false; clearTimeout(timer) }
  }, [model, project, objName])

  const templates = model?.form?.templates || []
  // Falls back to the first rather than to nothing: a key that no longer
  // exists (its template was just deleted) must leave the designer on a
  // template rather than on a blank screen.
  const templateIndex = Math.max(
    0, templates.findIndex((t) => t.key === templateKey))
  const template = templates[templateIndex]
  const composed = templates.some(
    (t) => (t.role || 'master') === 'master' && (t.sections || []).length > 0)
    || templates.some((t) => t.role === 'section')
    || templates.filter((t) => (t.role || 'master') === 'master').length > 1

  if (!model) {
    return <Shell busy={busy}>Loading…</Shell>
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '8px 12px', borderBottom: '1px solid var(--color-border)',
      }}>
        <strong style={{ fontSize: '1.0125rem' }}>
          {model.form.title || objName}
        </strong>
        <span style={{
          fontSize: '0.8425rem', textTransform: 'uppercase', letterSpacing: 0.6,
          padding: '2px 7px', borderRadius: 10,
          background: 'var(--color-bg-secondary)',
          color: 'var(--color-text-secondary)',
        }}>{model.form.governance}</span>
        <nav style={{ display: 'flex', gap: 4, marginLeft: 12 }}>
          {(['settings', 'sections', 'fields', 'body',
             'sign-off'] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              style={tabStyle(tab === t)}
            >{t === 'sign-off' ? 'Sign-off'
              : t[0].toUpperCase() + t.slice(1)}</button>
          ))}
        </nav>
        {/*
          * Shown only once there IS a choice. A form with one template is the
          * overwhelming case and a switcher offering one option is a control
          * that says "there is a decision here" when there is not.
          */}
        {composed && templates.length > 1
          && (tab === 'fields' || tab === 'body' || tab === 'sign-off') && (
          <label style={{
            display: 'flex', alignItems: 'center', gap: 6, marginLeft: 10,
            fontSize: '0.8425rem', color: 'var(--color-text-secondary)',
          }}>
            Editing
            <select
              value={template?.key || ''}
              onChange={(e) => setTemplateKey(e.target.value)}
              style={{
                padding: '3px 6px', borderRadius: 4,
                border: '1px solid var(--color-border)',
                background: 'var(--color-bg)', color: 'var(--color-text)',
              }}
            >
              <optgroup label="Masters — a record is opened on one of these">
                {templates.filter((t) => (t.role || 'master') === 'master')
                  .map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.title || t.key}
                    </option>
                  ))}
              </optgroup>
              {templates.some((t) => t.role === 'section') && (
                <optgroup label="Sections — composed into a master">
                  {templates.filter((t) => t.role === 'section').map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.title || t.key}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>
        )}
        <span style={{ flex: 1 }} />
        <ProblemChip problems={problems} />
        {dirty && <span style={{ fontSize: '0.8625rem', color: '#b45309' }}>
          Unsaved</span>}
        <button type="button" onClick={() => void save()} disabled={busy}
          style={buttonStyle(true)}>Save</button>
      </div>

      <div style={tab === 'body'
        // The Body tab is a three-pane surface that owns its own scrolling —
        // a padded `overflow: auto` around it would scroll the panels out of
        // view instead of the page they are looking at.
        ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
          padding: '0 12px 12px' }
        : { flex: 1, minHeight: 0, overflow: 'auto', padding: 16 }}>
        {tab === 'settings' && (
          <SettingsTab model={model} objects={objects} visuals={visuals}
            project={project} mutate={mutate} />
        )}
        {tab === 'sections' && (
          <SectionsTab model={model} mutate={mutate}
            onEdit={(key) => { setTemplateKey(key); setTab('body') }} />
        )}
        {tab === 'sign-off' && template && (
          <SignOffTab template={template} templateIndex={templateIndex}
            model={model} mutate={mutate} />
        )}
        {tab === 'fields' && template && (
          <FieldsTab template={template} templateIndex={templateIndex}
            mutate={mutate}
            project={project} objName={objName} model={model}
            objects={objects} />
        )}
        {tab === 'body' && template && (
          <Suspense fallback={<p style={{ fontSize: '0.9625rem',
            color: 'var(--color-text-secondary)' }}>Loading the editor…</p>}>
            <BodyTab template={template} templateIndex={templateIndex}
              mutate={mutate} model={model}
              project={project} objName={objName} />
          </Suspense>
        )}
      </div>
    </div>
  )
}

// ── what is wrong with this form ───────────────────────────────────────────

/**
 * The problems, as one chip beside Save.
 *
 * This replaced a five-step "Pick the record · Lay out the page · Place the
 * data · Mark what is entered · Publish" rail across the whole window. Every
 * step was ticked on any form worth opening, so the row cost a permanent strip
 * of screen to say "yes" five times — and the ONE thing it was there to
 * report, a blocking problem, was the part it said least clearly.
 *
 * So: nothing at all when there is nothing wrong, and the offender's own
 * sentence when there is. Driven by the same `er/validate.problems` the publish
 * gate and the runtime's `open_document` use — a copy of the rules in the
 * browser would drift, and it would drift toward saying yes.
 */
function ProblemChip({ problems }: {
  problems: { code: string; message: string; blocking?: boolean }[]
}) {
  const [open, setOpen] = useState(false)
  if (!problems.length) return null
  const blocking = problems.filter((p) => p.blocking)
  const worst = blocking.length ? blocking : problems
  const tone = blocking.length ? 'var(--color-error)' : '#b45309'

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={worst.map((p) => p.message).join('\n\n')}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '2px 9px',
          borderRadius: 999, cursor: 'pointer', fontSize: '0.8725rem',
          border: `1px solid ${tone}`, color: tone,
          background: 'transparent',
        }}
      >
        <span>{blocking.length ? '✕' : '!'}</span>
        {worst.length === 1 ? '1 problem' : `${worst.length} problems`}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 6,
          width: 380, zIndex: 40, padding: '10px 12px', borderRadius: 8,
          border: '1px solid var(--color-border)',
          background: 'var(--color-bg)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
        }}>
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.9325rem',
            lineHeight: 1.6 }}>
            {problems.map((p) => (
              <li key={p.code + p.message}
                style={{ color: p.blocking ? 'var(--color-error)'
                  : 'var(--color-text)' }}>
                {p.message}
                {p.blocking && (
                  <strong style={{ fontSize: '0.8725rem' }}> — blocks publishing</strong>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// ── settings ───────────────────────────────────────────────────────────────

function SettingsTab({ model, objects, visuals, project, mutate }: {
  model: FormModel
  objects: { obj_name: string }[]
  visuals: { obj_name: string }[]
  project: string
  mutate: (fn: (m: FormModel) => void) => void
}) {
  /*
   * The object catalog spells names as they are on disk (`crm_manage_deal`);
   * the model normaliser upper-cases them (`CRM_MANAGE_DEAL`). An exact compare
   * therefore matches no <option>, and the select renders as "— choose a
   * transaction —" over a form that has an anchor perfectly well — which reads
   * as "nothing is set here" and invites somebody to set it again.
   *
   * `ddlbSelectValue` is the shared matcher the transaction form already uses
   * for the same shape of problem (it normalises case AND whitespace); a second
   * copy of the rule here would be a second place for it to drift.
   */
  const objectOptions = objects.map((o) => ({ data: o.obj_name }))
  const pickObject = (stored: unknown) => ddlbSelectValue(stored, objectOptions)

  const form = model.form
  const opened = (form.open || {}) as Record<string, unknown>
  const capture = form.capture as Record<string, string>
  const iface = form.interface as Record<string, unknown>

  return (
    <div style={{ display: 'grid', gap: 18, maxWidth: 760 }}>
      <Field label="Title">
        <input value={form.title}
          onChange={(e) => mutate((m) => { m.form.title = e.target.value })}
          style={inputStyle} />
      </Field>
      <Field label="Description">
        <input value={form.description}
          onChange={(e) => mutate((m) => { m.form.description = e.target.value })}
          style={inputStyle} />
      </Field>

      <Field
        label="Governance"
        hint="How much ceremony the record carries. `none` costs nothing — no
              execution record, no controlled document, no signatures — and is
              right for an invoice or a delivery note. Climb only when somebody
              signs, audits or regulates this."
      >
        <select value={form.governance}
          onChange={(e) => mutate((m) => {
            m.form.governance = e.target.value as FormModel['form']['governance']
          })}
          style={inputStyle}>
          <option value="none">none — an ordinary business document</option>
          <option value="audited">audited — instance record, reason for change</option>
          <option value="regulated">regulated — signatures, step gating, deviations</option>
        </select>
      </Field>

      <KeysSection model={model} mutate={mutate} />
      <TransactionsSection model={model} objects={objects} visuals={visuals}
        project={project} mutate={mutate} pickObject={pickObject} />

      <fieldset style={groupStyle}>
        <legend style={legendStyle}>Opening</legend>
        <Field label="Title expression"
          hint="Shown at the top of the document. `{ctx.COLUMN}` reads the
                primary record; `{TXN.form.COLUMN}` reads any of them.">
          <input value={String(opened.title_expression || '')}
            onChange={(e) => mutate((m) => {
              (m.form.open as Record<string, string>).title_expression
                = e.target.value
            })}
            placeholder="Invoice {ctx.INVOICE_NO}"
            style={inputStyle} />
        </Field>
        <Check
          label="Only one open document per record"
          checked={!!opened.one_open_instance}
          onChange={(v) => mutate((m) => {
            (m.form.open as Record<string, boolean>).one_open_instance = v
          })}
        />
      </fieldset>

      <fieldset style={groupStyle}>
        <legend style={legendStyle}>Capture — where typed values go</legend>
        <Field label="Mode"
          hint="`typed` writes the transaction's own columns and is what an
                invoice wants. `generic` needs no DDL for a runtime-authored
                template — and is not available in this build.">
          <select value={String(capture.mode || 'typed')}
            onChange={(e) => mutate((m) => {
              (m.form.capture as Record<string, string>).mode = e.target.value
            })}
            style={inputStyle}>
            <option value="typed">typed — the object's own columns</option>
            <option value="generic">generic — a canonical capture transaction</option>
          </select>
        </Field>
        <Field label="Target object"
          hint="Leave empty to write to the anchor itself, which is the usual
                case.">
          <select value={pickObject(capture.obj_name)}
            onChange={(e) => mutate((m) => {
              (m.form.capture as Record<string, string>).obj_name = e.target.value
            })}
            style={inputStyle}>
            <option value="">— the anchor —</option>
            {objects.map((o) => (
              <option key={o.obj_name} value={o.obj_name}>{o.obj_name}</option>
            ))}
          </select>
        </Field>
      </fieldset>

      <fieldset style={groupStyle}>
        <legend style={legendStyle}>Interface</legend>
        <Check
          label="Offer this as a view of the transaction"
          hint="Adds Document beside List / Spread sheet / Form input on the
                anchor transaction's own screen."
          checked={iface.in_switcher !== false}
          onChange={(v) => mutate((m) => {
            (m.form.interface as Record<string, boolean>).in_switcher = v
          })}
        />
        <Field label="Called">
          <input value={String(iface.register_as || 'Document')}
            onChange={(e) => mutate((m) => {
              (m.form.interface as Record<string, string>).register_as
                = e.target.value
            })}
            style={inputStyle} />
        </Field>
      </fieldset>
    </div>
  )
}

// ── keys and transactions (§4A) ────────────────────────────────────────────

/**
 * The document's primary key — ONE identity for every transaction it covers.
 *
 * First on the screen because it is first in the design: the keys are what each
 * transaction is looked up by, so a transactions list authored before them has
 * nothing to map. A key marked `generated` is allocated by the primary at save
 * and is never prompted — a box for an auto-generated number invites somebody
 * to type one the save discards.
 */
function KeysSection({ model, mutate }: {
  model: FormModel
  mutate: (fn: (m: FormModel) => void) => void
}) {
  const keys = model.form.keys || []
  // Whether this form is a face on a VISUAL decides what a key can BE: only a
  // visual has rows to split, so only there does `row` mean anything.
  const primaryIsVisual =
    (model.form.transactions || [])[0]?.kind === 'visual'
  const identity = keys.filter((k) => k.source === 'row')
  return (
    <fieldset style={groupStyle}>
      <legend style={legendStyle}>Keys — what identifies this document</legend>
      <p style={hintStyle}>
        Every source below is loaded by these, through its own column
        mapping. Opened from a transaction&rsquo;s own list, they are taken from
        the record you picked; from the menu, the operator is asked for them.
      </p>
      {primaryIsVisual && (
        <p style={hintStyle}>
          On a visual, a key marked <strong>from the result</strong> is what
          splits one run into many documents — one per distinct value. Run a
          customer agreement for a whole state with{' '}
          <code>FIN_ENTITY_ID</code> as the identity and you get one agreement
          per financial entity, each listing that entity&rsquo;s own rows.
          {identity.length === 0 && ' With none, the whole run is one document.'}
        </p>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse',
        fontSize: '0.9425rem' }}>
        <thead>
          <tr>{['Key', 'Prompt', 'Type', 'Source', ''].map((h) => (
            <th key={h} style={thStyle}>{h}</th>))}</tr>
        </thead>
        <tbody>
          {keys.map((row, index) => (
            <tr key={index}>
              <td style={tdStyle}>
                <input value={row.key}
                  onChange={(e) => mutate((m) => {
                    m.form.keys[index].key = e.target.value
                  })} style={cellInput} />
              </td>
              <td style={tdStyle}>
                <input value={row.title || ''}
                  onChange={(e) => mutate((m) => {
                    m.form.keys[index].title = e.target.value
                  })} style={cellInput} />
              </td>
              <td style={tdStyle}>
                <select value={row.data_type}
                  onChange={(e) => mutate((m) => {
                    m.form.keys[index].data_type = e.target.value
                  })} style={cellInput}>
                  {['VARCHAR', 'NUMERIC', 'DATE'].map((t) => (
                    <option key={t} value={t}>{t}</option>))}
                </select>
              </td>
              <td style={tdStyle}>
                <select value={row.source || 'prompt'}
                  onChange={(e) => mutate((m) => {
                    m.form.keys[index].source
                      = e.target.value as KeyRow['source']
                  })} style={cellInput}>
                  <option value="prompt">prompt — the operator supplies it</option>
                  {/* Nothing allocates a visual row, and nothing splits a
                      transaction's records — so each option is offered only
                      where it means something. An option that is always shown
                      and sometimes ignored is worse than one that is absent. */}
                  {!primaryIsVisual && (
                    <option value="generated">generated — the record allocates it</option>
                  )}
                  {primaryIsVisual && (
                    <option value="row">from the result — one document per value</option>
                  )}
                </select>
              </td>
              <td style={tdStyle}>
                <button type="button"
                  onClick={() => mutate((m) => { m.form.keys.splice(index, 1) })}
                  style={{ ...buttonStyle(false), padding: '0 8px' }}>×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div>
        <button type="button" style={buttonStyle(false)}
          onClick={() => mutate((m) => {
            m.form.keys = [...(m.form.keys || []), {
              key: '', title: '', data_type: 'VARCHAR', mandatory: true,
              source: 'prompt',
            }]
          })}>Add key</button>
      </div>
    </fieldset>
  )
}

/**
 * The transactions this document covers — primary first (D22).
 *
 * The primary hosts the Document view on its own screen and is what
 * `{ctx.…}` names; the rest are loaded by the same keys, rendered, calculated
 * over, and written unless they are read-only. Each one says what to do when
 * there is no record for the keys, which is the decision that used to be one
 * document-wide `create` / `open` switch and stopped making sense the moment
 * there was more than one transaction.
 */
function TransactionsSection({ model, objects, visuals, project, mutate,
                              pickObject }: {
  model: FormModel
  objects: { obj_name: string }[]
  visuals: { obj_name: string }[]
  project: string
  mutate: (fn: (m: FormModel) => void) => void
  pickObject: (stored: unknown) => string
}) {
  const txns = model.form.transactions || []
  const keys = (model.form.keys || []).map((k) => k.key).filter(Boolean)

  return (
    <fieldset style={groupStyle}>
      <legend style={legendStyle}>Data sources — what it reads and writes</legend>
      <p style={hintStyle}>
        The first is the <strong>primary</strong>: the source whose screen
        offers this as its Document view, and the one <code>{'{ctx.…}'}</code>
        {' '}names. Everything else is linked. A source is a{' '}
        <strong>transaction</strong> (one record, filled in) or a{' '}
        <strong>visual</strong> (a result set — read only, and one document per
        identity).
      </p>
      {txns.map((txn, index) => (
        <div key={index} style={{
          border: '1px solid var(--color-border)', borderRadius: 6,
          padding: 10, display: 'grid', gap: 8,
        }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{
              fontSize: '0.8225rem', textTransform: 'uppercase', letterSpacing: 0.5,
              padding: '2px 6px', borderRadius: 8,
              background: index === 0 ? 'var(--color-accent)' : 'var(--color-bg-secondary)',
              color: index === 0 ? '#fff' : 'var(--color-text-secondary)',
            }}>{index === 0 ? 'primary' : 'linked'}</span>
            <select value={pickObject(txn.obj_name)}
              onChange={(e) => mutate((m) => {
                const row = m.form.transactions[index]
                row.obj_name = e.target.value
                // WHICH catalogue it came from decides the kind. Inferring it
                // from the name would be a guess, and a visual read as a
                // transaction fails later with a message about a missing
                // model file rather than here with the truth.
                const isVisual = visuals.some(
                  (v) => v.obj_name.toUpperCase()
                    === String(e.target.value).toUpperCase())
                row.kind = isVisual ? 'visual' : 'transaction'
                if (isVisual) {
                  // The shape a visual can only have. Written here so the
                  // screen shows the truth immediately rather than after a
                  // save-and-reload through the normaliser.
                  row.read_only = true
                  row.cardinality = 'many'
                  row.form_no = '1'
                  row.rows = { ...(row.rows || { forms: [] }), auto_add: false }
                  if (row.on_missing === 'create') row.on_missing = 'skip'
                }
              })}
              style={{ ...inputStyle, maxWidth: 260 }}>
              <option value="">— choose a source —</option>
              {objects.length > 0 && (
                <optgroup label="Transactions">
                  {objects.map((o) => (
                    <option key={`t-${o.obj_name}`} value={o.obj_name}>
                      {o.obj_name}
                    </option>
                  ))}
                </optgroup>
              )}
              {visuals.length > 0 && (
                <optgroup label="Visuals">
                  {visuals.map((o) => (
                    <option key={`v-${o.obj_name}`} value={o.obj_name}>
                      {o.obj_name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <input value={txn.alias || ''} placeholder="short name (optional)"
              title="What a path calls it — DN.2.ITEM_CODE rather than the full object name"
              onChange={(e) => mutate((m) => {
                m.form.transactions[index].alias = e.target.value
              })}
              style={{ ...inputStyle, width: 170 }} />
            <span style={{ flex: 1 }} />
            {index > 0 && (
              <button type="button" title="Make this the primary"
                onClick={() => mutate((m) => {
                  const [moved] = m.form.transactions.splice(index, 1)
                  moved.role = 'primary'
                  m.form.transactions.forEach((t) => { t.role = 'linked' })
                  m.form.transactions.unshift(moved)
                })}
                style={{ ...buttonStyle(false), padding: '0 8px' }}>↑</button>
            )}
            <button type="button"
              onClick={() => mutate((m) => {
                m.form.transactions.splice(index, 1)
                if (m.form.transactions[0]) m.form.transactions[0].role = 'primary'
              })}
              style={{ ...buttonStyle(false), padding: '0 8px' }}>×</button>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'grid', gap: 3 }}>
              <span style={microLabel}>
                {txn.kind === 'visual' ? 'When it returns no rows'
                  : 'When there is no record'}
              </span>
              <select value={txn.on_missing || 'refuse'}
                onChange={(e) => mutate((m) => {
                  m.form.transactions[index].on_missing
                    = e.target.value as TxnRow['on_missing']
                })}
                style={{ ...inputStyle, width: 300 }}>
                <option value="refuse">refuse — say so and do not open</option>
                {/* Nothing creates a visual, so the option is not offered
                    rather than offered and quietly ignored. */}
                {txn.kind !== 'visual' && (
                  <option value="create">create — open a new one on these keys</option>
                )}
                <option value="skip">skip — leave this part of the document empty</option>
              </select>
            </label>
            {txn.kind === 'visual' ? (
              // Not a row of disabled checkboxes. A visual is read-only and
              // single-form because of what it IS, and a greyed-out tick box
              // invites somebody to wonder how to untick it.
              <span style={{ ...hintStyle, marginTop: 16, maxWidth: 360 }}>
                A visual is read only: its columns are worked out from other
                data, so the document can show and total them but never write
                to them. Extra fields you add are kept with the document.
              </span>
            ) : (
              <>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center',
                  marginTop: 14 }}>
                  <input type="checkbox" checked={!!txn.read_only}
                    onChange={(e) => mutate((m) => {
                      m.form.transactions[index].read_only = e.target.checked
                      if (e.target.checked) {
                        // The two cannot both be true, and saying so here beats
                        // letting the save report it afterwards.
                        m.form.transactions[index].rows.auto_add = false
                      }
                    })} />
                  <span style={{ fontSize: '0.9425rem' }}>Read only</span>
                </label>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center',
                  marginTop: 14 }}>
                  <input type="checkbox" checked={!!txn.rows?.auto_add}
                    disabled={!!txn.read_only}
                    onChange={(e) => mutate((m) => {
                      m.form.transactions[index].rows.auto_add = e.target.checked
                    })} />
                  <span style={{ fontSize: '0.9425rem' }}>Rows may be added</span>
                </label>
              </>
            )}
          </div>

          {keys.length > 0 && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {keys.map((k) => (
                <label key={k} style={{ display: 'grid', gap: 3 }}>
                  <span style={microLabel}>
                    {k} is {txn.kind === 'visual'
                      ? 'its column / prompt' : 'held in'}
                  </span>
                  <input
                    value={txn.key_map?.[k] || ''}
                    placeholder={k}
                    onChange={(e) => mutate((m) => {
                      m.form.transactions[index].key_map = {
                        ...(m.form.transactions[index].key_map || {}),
                        [k]: e.target.value,
                      }
                    })}
                    style={{ ...inputStyle, width: 190 }} />
                </label>
              ))}
            </div>
          )}
        </div>
      ))}
      <div>
        <button type="button" style={buttonStyle(false)}
          onClick={() => mutate((m) => {
            m.form.transactions = [...(m.form.transactions || []), {
              obj_name: '', alias: '',
              role: m.form.transactions?.length ? 'linked' : 'primary',
              kind: 'transaction',
              form_no: '1', key_map: {}, cardinality: 'one',
              on_missing: 'refuse', read_only: false,
              rows: { auto_add: true, forms: [] },
            }]
          })}>Add source</button>
      </div>
    </fieldset>
  )
}

// ── small pieces ───────────────────────────────────────────────────────────

function Shell({ children, busy }: { children: React.ReactNode; busy: boolean }) {
  return (
    <div style={{ padding: 24, fontSize: 14,
      color: 'var(--color-text-secondary)' }}>
      {busy ? 'Loading…' : children}
    </div>
  )
}

function Field({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode
}) {
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: '0.8925rem', fontWeight: 600,
        color: 'var(--color-text-secondary)' }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: '0.8725rem',
        color: 'var(--color-text-secondary)' }}>{hint}</span>}
    </label>
  )
}

function Check({ label, hint, checked, onChange }: {
  label: string; hint?: string; checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <input type="checkbox" checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 3 }} />
      <span>
        <span style={{ fontSize: '0.9025rem' }}>{label}</span>
        {hint && <span style={{ display: 'block', fontSize: '0.8725rem',
          color: 'var(--color-text-secondary)' }}>{hint}</span>}
      </span>
    </label>
  )
}

const hintStyle: React.CSSProperties = {
  fontSize: '0.9125rem', color: 'var(--color-text-secondary)', margin: 0,
}

const microLabel: React.CSSProperties = {
  fontSize: '0.8425rem', fontWeight: 600, color: 'var(--color-text-secondary)',
}

const cellInput: React.CSSProperties = {
  ...inputStyle, height: 26, fontSize: '0.9325rem', minWidth: 90,
}

const groupStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 8, padding: 14,
  display: 'grid', gap: 12,
}

const legendStyle: React.CSSProperties = {
  fontSize: '0.8925rem', fontWeight: 700, letterSpacing: 0.4,
  textTransform: 'uppercase', color: 'var(--color-text-secondary)',
  padding: '0 6px',
}

const thStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)', padding: '4px 6px',
  textAlign: 'left', background: 'var(--color-bg-secondary)',
  fontSize: '0.8725rem', fontWeight: 700,
}

const tdStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)', padding: '3px 5px',
  verticalAlign: 'top',
}

function tabStyle(active: boolean): React.CSSProperties {
  return {
    height: 26, padding: '0 12px', borderRadius: 6, fontSize: '0.9325rem',
    cursor: 'pointer',
    border: `1px solid ${active ? 'var(--color-accent)' : 'transparent'}`,
    background: active ? 'var(--color-bg-secondary)' : 'transparent',
    color: 'var(--color-text)',
  }
}

